/** Units of measure with translations. */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { type UnitResponse } from "@offertli/shared";
import { Button } from "../../components/ui/Button.js";
import { Field } from "../../components/ui/Field.js";
import { InputText as Input } from "@primereact/ui/inputtext";
import { Dialog as Modal } from "@primereact/ui/dialog";
import { ToggleSwitch as Toggle } from "@primereact/ui/toggleswitch";
import { apiGet, apiPatch, apiPost } from "../../lib/api.js";
import { MutationError, Section } from "../shared.js";

export function UnitsForm() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [activeOnly, setActiveOnly] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [customTranslations, setCustomTranslations] = useState(false);
  const [translationNames, setTranslationNames] = useState({
    de: "",
    fr: "",
    es: "",
  });
  const locale = i18n.resolvedLanguage?.split("-")[0];
  const query = useQuery({
    queryKey: ["units"],
    queryFn: () =>
      apiGet<{
        data: UnitResponse[];
      }>("/settings/units").then((response) => response.data),
  });
  const localizedName = (unit: UnitResponse): string => {
    const custom =
      locale === "de" || locale === "fr" || locale === "es"
        ? unit.translations[locale]
        : undefined;
    return (
      custom ??
      t(`settings.unitNames.${unit.code}`, {
        defaultValue: unit.name,
      })
    );
  };
  const resetCreate = () => {
    setCode("");
    setName("");
    setCustomTranslations(false);
    setTranslationNames({
      de: "",
      fr: "",
      es: "",
    });
  };
  const create = useMutation({
    mutationFn: () => {
      const trimmedName = name.trim();
      return apiPost("/settings/units", {
        code: code.trim(),
        name: trimmedName,
        translations: Object.fromEntries(
          (["de", "fr", "es"] as const).map((language) => [
            language,
            {
              name:
                (customTranslations
                  ? translationNames[language].trim()
                  : trimmedName) || trimmedName,
            },
          ]),
        ),
      });
    },
    onSuccess: async () => {
      setCreateOpen(false);
      resetCreate();
      await queryClient.invalidateQueries({
        queryKey: ["units"],
      });
    },
  });
  const update = useMutation({
    mutationFn: ({
      unit,
      data,
    }: {
      unit: UnitResponse;
      data: Record<string, unknown>;
    }) => apiPatch(`/settings/units/${unit.id}`, data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["units"],
      });
    },
  });
  const reorder = useMutation({
    mutationFn: async ({
      unit,
      direction,
    }: {
      unit: UnitResponse;
      direction: -1 | 1;
    }) => {
      const allUnits = query.data ?? [];
      const index = allUnits.findIndex((candidate) => candidate.id === unit.id);
      const adjacent = allUnits[index + direction];
      if (!adjacent) return;
      await Promise.all([
        apiPatch(`/settings/units/${unit.id}`, {
          sortOrder: adjacent.sortOrder,
        }),
        apiPatch(`/settings/units/${adjacent.id}`, {
          sortOrder: unit.sortOrder,
        }),
      ]);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["units"],
      });
    },
  });
  const renameUnit = (unit: UnitResponse) => {
    const nextName = window
      .prompt(
        t("settings.unitName", {
          defaultValue: "Unit name",
        }),
        localizedName(unit),
      )
      ?.trim();
    if (!nextName || nextName === localizedName(unit)) return;
    update.mutate({
      unit,
      data: {
        name: nextName,
        ...(locale === "de" || locale === "fr" || locale === "es"
          ? {
              translations: {
                [locale]: {
                  name: nextName,
                },
              },
            }
          : {}),
      },
    });
  };
  const allUnits = query.data ?? [];
  const visibleUnits = allUnits.filter((unit) => !activeOnly || unit.active);
  return (
    <Section
      title={t("settings.units", {
        defaultValue: "Units",
      })}
    >
      <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
        <div>
          <label
            className="flex items-center gap-3 text-sm font-medium"
            htmlFor="units-active-only"
          >
            <Toggle.Root
              inputId="units-active-only"
              checked={activeOnly}
              onCheckedChange={(event: { checked: boolean }) =>
                setActiveOnly(event.checked)
              }
            >
              <Toggle.Control>
                <Toggle.Handle></Toggle.Handle>
              </Toggle.Control>
            </Toggle.Root>
            <span>
              {t("settings.activeOnly", {
                defaultValue: "Active only",
              })}
            </span>
          </label>
        </div>
        <Button
          onClick={() => {
            resetCreate();
            setCreateOpen(true);
          }}
        >
          {t("settings.addUnit", {
            defaultValue: "Add unit",
          })}
        </Button>
      </div>
      <div className="space-y-2">
        {visibleUnits.map((unit) => {
          const index = allUnits.findIndex(
            (candidate) => candidate.id === unit.id,
          );
          return (
            <div
              key={unit.id}
              className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-app-border p-3"
            >
              <span>
                <strong>{localizedName(unit)}</strong>
                <span className="ml-2 text-sm text-app-muted">{unit.code}</span>
              </span>
              <div className="flex flex-wrap items-center justify-end gap-1">
                <Button
                  size="small"
                  variant="text"
                  disabled={index <= 0 || reorder.isPending}
                  aria-label={t("common.moveUp", {
                    defaultValue: "Move up",
                  })}
                  onClick={() =>
                    reorder.mutate({
                      unit,
                      direction: -1,
                    })
                  }
                >
                  ↑
                </Button>
                <Button
                  size="small"
                  variant="text"
                  disabled={
                    index < 0 ||
                    index >= allUnits.length - 1 ||
                    reorder.isPending
                  }
                  aria-label={t("common.moveDown", {
                    defaultValue: "Move down",
                  })}
                  onClick={() =>
                    reorder.mutate({
                      unit,
                      direction: 1,
                    })
                  }
                >
                  ↓
                </Button>
                <Button
                  size="small"
                  variant="text"
                  onClick={() => renameUnit(unit)}
                >
                  {t("common.rename", {
                    defaultValue: "Rename",
                  })}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  severity="secondary"
                  loading={update.isPending || undefined}
                  onClick={() =>
                    update.mutate({
                      unit,
                      data: {
                        active: !unit.active,
                      },
                    })
                  }
                >
                  {unit.active
                    ? t("common.deactivate", {
                        defaultValue: "Deactivate",
                      })
                    : t("common.activate", {
                        defaultValue: "Activate",
                      })}
                </Button>
              </div>
            </div>
          );
        })}
      </div>
      <Modal.Root
        open={createOpen}
        onOpenChange={(event: { value: boolean | undefined }) =>
          !event.value &&
          (() => {
            if (!create.isPending) setCreateOpen(false);
          })()
        }
      >
        <Modal.Portal>
          <Modal.Backdrop></Modal.Backdrop>
          <Modal.Positioner>
            <Modal.Popup>
              <Modal.Header>
                <Modal.Title>
                  {t("settings.addUnit", {
                    defaultValue: "Add unit",
                  })}
                </Modal.Title>
                <Modal.HeaderActions>
                  <Modal.Close className="pi pi-times"></Modal.Close>
                </Modal.HeaderActions>
              </Modal.Header>
              <Modal.Content>
                <form
                  className="space-y-4"
                  onSubmit={(event) => {
                    event.preventDefault();
                    create.mutate();
                  }}
                >
                  <Field
                    label={t("settings.unitCode", {
                      defaultValue: "Unit code",
                    })}
                    hint={t("settings.unitCodeHint", {
                      defaultValue:
                        "Short stable code used in documents, for example min or bag.",
                    })}
                    required
                  >
                    <Input
                      required
                      maxLength={20}
                      value={code}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        setCode(event.target.value)
                      }
                    />
                  </Field>
                  <Field
                    label={t("settings.unitName", {
                      defaultValue: "Unit name",
                    })}
                    required
                  >
                    <Input
                      required
                      maxLength={80}
                      value={name}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        setName(event.target.value)
                      }
                    />
                  </Field>
                  <div>
                    <label
                      className="flex items-center gap-3 text-sm font-medium"
                      htmlFor="unit-custom-translations"
                    >
                      <Toggle.Root
                        inputId="unit-custom-translations"
                        checked={customTranslations}
                        onCheckedChange={(event: { checked: boolean }) =>
                          ((checked) => {
                            setCustomTranslations(checked);
                            if (checked) {
                              setTranslationNames((current) => ({
                                de: current.de || name,
                                fr: current.fr || name,
                                es: current.es || name,
                              }));
                            }
                          })(event.checked)
                        }
                      >
                        <Toggle.Control>
                          <Toggle.Handle></Toggle.Handle>
                        </Toggle.Control>
                      </Toggle.Root>
                      <span>
                        {t("settings.customTranslations", {
                          defaultValue: "Custom translations",
                        })}
                      </span>
                    </label>
                  </div>
                  {customTranslations ? (
                    <div className="grid gap-3 sm:grid-cols-3">
                      {(["de", "fr", "es"] as const).map((language) => (
                        <Field
                          key={language}
                          label={t(`locale.${language}`)}
                          required
                        >
                          <Input
                            required
                            value={translationNames[language]}
                            onChange={(
                              event: React.ChangeEvent<HTMLInputElement>,
                            ) =>
                              setTranslationNames((current) => ({
                                ...current,
                                [language]: event.target.value,
                              }))
                            }
                          />
                        </Field>
                      ))}
                    </div>
                  ) : null}
                  <MutationError error={create.error} />
                  <div className="flex justify-end gap-2">
                    <Button
                      type="button"
                      variant="outlined"
                      severity="secondary"
                      disabled={create.isPending}
                      onClick={() => setCreateOpen(false)}
                    >
                      {t("common.cancel")}
                    </Button>
                    <Button
                      type="submit"
                      loading={create.isPending || undefined}
                    >
                      {t("common.add")}
                    </Button>
                  </div>
                </form>
              </Modal.Content>
            </Modal.Popup>
          </Modal.Positioner>
        </Modal.Portal>
      </Modal.Root>
      <MutationError error={update.error || reorder.error || query.error} />
    </Section>
  );
}
